import {Map} from 'immutable'
import PropTypes from 'prop-types'

import AccessibleSmallModal from '../../../../../shared_components/mui_base_components/modals/accessible_small_modal/AccessibleSmallModal'
import Button from '../../../../../shared_components/mui_base_components/button/Button'
import LinkButton from '../../../../../shared_components/mui_base_components/link_button/LinkButton'
import Loader from '../../../../../shared_components/NewLoader'
import Logger from '../../../../../lib/NewLogger'
import {enterLifeStoryFlow} from './lifeStoryFlowEntry'
import {trackLifeStoryEvent} from './lifeStoryEventLogger'
import {commitPendingDeletion, usePendingLifeStoryDeletion} from './lifeStoryPendingDeletion'
import useEthicalWills from './hooks/useEthicalWills'
import {
  LIFE_STORY_FLOW_PATH,
  latestEthicalWill,
  lifeStoryContinuePath
} from './lifeStoryTools'

import './addLifeStoryChooser.scss'

const CHOOSER_TITLE_ID = 'ls-chooser-title'

const AddLifeStoryChooser = ({exit, addClassic, userConfig}) => {
  const {loadingEthicalWills, ethicalWillsUnavailable, ethicalWills} = useEthicalWills()
  const pendingDeletion = usePendingLifeStoryDeletion()

  // A story inside its undo-after-delete window is already gone as far as the
  // user is concerned; only the undo bar can bring it back.
  const visibleEthicalWills = pendingDeletion ?
    ethicalWills.filter(will => will.get('id') !== pendingDeletion.ethicalWillId) :
    ethicalWills
  // Fall back to begin when the fetch failed — LifeStoryFlow bails to the
  // category page on a failed fetch, so starting from here can't create a
  // duplicate. While loading, wait so the wrong variant never flashes.
  const ethicalWill = ethicalWillsUnavailable ? null : latestEthicalWill(visibleEthicalWills)
  const continuing = Boolean(ethicalWill)

  const startGuided = () => {
    if (continuing) {
      Logger.log({name: 'clicked_continue_life_story', payload: {context: 'add_chooser', type: 'vault'}})
      trackLifeStoryEvent({eventName: 'click_my_story_item_entry', userConfig, payload: {context: 'add_chooser'}})
      enterLifeStoryFlow(lifeStoryContinuePath(ethicalWill.get('id')), {openPicker: true})

      return
    }

    // The pending record still exists server-side until the undo window ends;
    // commit it before inviting a create so uniqueness isn't violated.
    if (pendingDeletion) commitPendingDeletion()

    Logger.log({name: 'clicked_begin_life_story', payload: {context: 'add_chooser', type: 'vault'}})
    enterLifeStoryFlow(LIFE_STORY_FLOW_PATH)
  }

  // The classic item form opens on this same page, so the chooser has to
  // dismiss itself first or the form would stack on top of it.
  const addItYourself = () => {
    exit()
    addClassic()
  }

  return (
    <AccessibleSmallModal
      className='add-life-story-chooser'
      closerLabel='Close'
      labelledBy={CHOOSER_TITLE_ID}
      onClose={exit}
      open={true}>
      <h2 className='ls-chooser-title' id={CHOOSER_TITLE_ID}>Add your story</h2>
      {
        loadingEthicalWills && !ethicalWillsUnavailable ?
          <Loader loading={true} /> :
          <>
            <div className='ls-chooser-guided'>
              <span className='ls-chooser-pill'>Recommended</span>
              <h3 className='ls-chooser-guided-title'>
                {continuing ? 'Continue with your story' : 'Start a guided story'}
              </h3>
              <p className='ls-chooser-guided-copy'>
                Answer one question at a time &mdash; written or in your own voice, and come back whenever.
              </p>
              <Button onClick={startGuided}>
                {continuing ? 'Continue my story' : 'Begin my story'}
              </Button>
            </div>
            <div className='ls-chooser-secondary'>
              <LinkButton onClick={addItYourself}>Already written something? Upload it instead.</LinkButton>
            </div>
          </>
      }
    </AccessibleSmallModal>
  )
}

AddLifeStoryChooser.propTypes = {
  addClassic: PropTypes.func,
  exit: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map)
}

export default AddLifeStoryChooser
